<template>
  <teleport to="body">
    <!-- Backdrop -->
    <div v-if="modelValue" :class="$style.backdrop" @click="close" />

    <!-- Slide-down panel -->
    <div :class="[$style.panel, modelValue ? $style.panelOpen : '']">
      <div :class="$style.headerRow">
        <div :class="$style.title">
          {{ isModerator ? $t("moderator-menu") : $t("host-menu") }}
        </div>
        <div title="Close this box" :class="[$style.closeIconContainer]" @click="close">
          <img :class="$style.closeIcon" :src="closeIcon" alt="close" />
        </div>
      </div>

      <div :class="$style.body">
        <!-- Replace these placeholders with real content later -->
        <div v-if="user.name" :class="$style.item">
          <div :class="$style.itemLabel">{{ $t("name") }}</div>
          <div :class="$style.itemText">{{ user.name }}</div>
        </div>
        <div v-if="isModerator && host?.name" :class="$style.item">
          <div :class="$style.itemLabel">{{ $t("host-name") }}</div>
          <div :class="$style.itemText">{{ host.name }}</div>
        </div>
        <template v-if="['JOIN'].includes(phase)">
          <div v-if="readingTime" :class="$style.item">
            <div :class="$style.itemLabel">{{ $t("reading-time") }}</div>
            <div :class="$style.itemText">{{ readingTime }} <span :class="$style.itemSubText">{{ $t("min") }}</span></div>
          </div>
          <div v-if="discussionTime" :class="$style.item">
            <div :class="$style.itemLabel">{{ $t("discussion-time") }}</div>
            <div :class="$style.itemText">{{ discussionTime }} <span :class="$style.itemSubText">{{ $t("min") }}</span></div>
          </div>
        </template>
        <template v-else>
          <div v-if="joinCode" :class="$style.item">
            <div :class="$style.itemLabel">{{ $t("join-code") }}</div>
            <div :class="$style.itemText">{{ joinCode }}</div>
          </div>
        </template>

        <div v-if="realTextLabel || canToggleOriginalText" :class="$style.item">
          <div :class="$style.itemLabel">{{ $t("orig-text") }}</div>
          <div
            :class="[
              $style.itemText,
              $globalStyle.headerItemHostSaboteurText,
              canToggleOriginalText &&
                !hostShowSaboteurText &&
                $globalStyle.blurredSaboteurText,
            ]"
          >
            {{ $t("text") }}
            {{
              canToggleOriginalText
                ? hostShowSaboteurText
                  ? realTextLabel || "—"
                  : "X"
                : realTextLabel
            }}
          </div>
          <HeaderItemButton
            v-if="canToggleOriginalText"
            :text="$t('show-saboteur-text')"
            :aria-label="
              hostShowSaboteurText ? 'Hide original text' : 'Show original text'
            "
            :iconLink="hostShowSaboteurText ? secretVisibleIcon : secretHideIcon"
            :clickFunction="toggleHostShowSaboteurText"
            backBg="#000000"
            frontBg="#676767"
            textColor="#ffc642"
            :icon-only="true"
            :button-width="'36px'"
            :button-height="'28px'"
          />
        </div>

        <div v-if="groups && groups.length" :class="$style.headerItem">
          <HeaderItemButton
            :text="$t('show-saboteurs')"
            :clickFunction="showGroupSaboteurs"
            backBg="#000000"
            frontBg="#676767"
            textColor="#ffc642"
            :button-width="isMobile ? '64px' : isTablet ? '80px' : '100px'"
          />
        </div>

        <div
          v-if="canShowWinners()"
          :class="$style.headerItem"
        >
          <HeaderItemButton
            :text="$t('show-winners')"
            :clickFunction="showGroupWinners"
            backBg="#000000"
            frontBg="#676767"
            textColor="#ffc642"
            :button-width="isMobile ? '64px' : isTablet ? '80px' : '100px'"
          />
        </div>

        <div v-if="gameWinner" :class="$style.item">
          <div :class="$style.itemLabel">{{ gameWinner === "None" ? $t("result") : $t("game-winner") }}</div>
          <div :class="[$style.itemText, $globalStyle.headerItemHostWinnerTextMobile]">
            {{ gameWinner === "None" 
                ? $t("game-tied") 
                : gameWinner === "Players" 
                  ? $t("players") 
                  : gameWinner === "Saboteur" 
                    ? $t("saboteur") 
                    : gameWinner 
            }}</div>
          </div>
        <div :class="[$globalStyle.headerHostChip, $style.hostChip]">
          <img :src="detectiveHatIcon" alt="Header" :class="$globalStyle.headerHostChipIcon" />
          <div :class="$globalStyle.headerHostChipText">{{ roleChipText }}</div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { computed } from "vue";
import styles from "@/css/mobileHeader.module.css";
import globalStyles from "@/css/global.module.css";
import CloseIcon from "@/assets/icons/close-red.svg";
import DetectiveHatIcon from "@/assets/icons/detective-hat.svg";
import SecretVisibleIcon from "@/assets/icons/secret-visible.svg";
import SecretHideIcon from "@/assets/icons/secret-hide.svg";
import HeaderItemButton from "../buttons/HeaderItemButton.vue";
import { useGameStore } from "@/stores/gameStore";
import { useI18n } from "vue-i18n";
const { t } = useI18n();

const $style = styles;
const $globalStyle = globalStyles;
const closeIcon = CloseIcon;
const detectiveHatIcon = DetectiveHatIcon;
const secretVisibleIcon = SecretVisibleIcon;
const secretHideIcon = SecretHideIcon;
const gameStore = useGameStore();

// eslint-disable-next-line
const props = defineProps({
  modelValue: { type: Boolean, required: true },
  user: { type: Object, required: true },
  phase: { type: String, required: false, default: "" },
  readingTime: { type: Number, required: true },
  discussionTime: { type: Number, required: true },
  joinCode: { type: String || Number, required: true },
  realTextLabel: { type: String, required: false, default: "" },
  groups: { type: Array, required: false, default: () => [] },
  showGroupSaboteurs: { type: Function, required: true },
  showGroupWinners: { type: Function, required: true },
  gameWinner: { type: String, required: false, default: "" },
  isModerator: { type: Boolean, default: false },
  host: { type: Object, required: false, default: null},
});

// eslint-disable-next-line
const emit = defineEmits(["update:modelValue"]);

const hostShowSaboteurText = computed(() => {
  return props.isModerator
    ? gameStore.moderatorShowSaboteurText
    : gameStore.hostShowSaboteurText;
});
const canToggleOriginalText = computed(() => !!props.phase);
const roleChipText = computed(() => {
  return props.isModerator ? t('moderator') : t('host');
});

function close() {
  emit("update:modelValue", false);
}

function toggleHostShowSaboteurText() {
  if (props.isModerator) {
        useGameStore().toggleModeratorShowSaboteurText();
      } else {
        useGameStore().toggleHostShowSaboteurText();
      }
}
function canShowWinners() {
  return (
    Array.isArray(props.groups) &&
    props.groups.length > 0 &&
    ["REVEAL", "DEBRIEF"].includes(props.phase)
  );
}
</script>

<style scoped></style>
